import { useId } from 'react'
import * as m from '~/paraglide/messages'
import { createFieldValidators } from '~/shared/lib/form-utils'
import { inviteBusinessSchema } from '~/shared/types/admin'
import { businessFieldsSchema, ownerFieldsSchema } from '~/shared/types/auth'
import { Checkbox } from '~/shared/ui/checkbox'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'
import { authLinkClassName } from './auth-shell'

interface FormFieldProps {
  // biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API is dynamically typed
  form: any
  focusedField: string | null
  setFocusedField: (field: string | null) => void
}

export function NameFields({ form, focusedField, setFocusedField }: FormFieldProps) {
  return (
    <>
      <form.Field
        name="firstName"
        validators={createFieldValidators(ownerFieldsSchema.shape.firstName)}
      >
        {(field: {
          name: string
          state: {
            value: string
            meta: { isTouched: boolean; errors: string[] }
          }
          handleBlur: () => void
          handleChange: (value: string) => void
        }) => {
          const isFocused = focusedField === field.name
          const showError =
            field.state.meta.isTouched && field.state.meta.errors.length > 0 && !isFocused
          return (
            <div className="space-y-2">
              <Label htmlFor={field.name}>{m.firstName()}</Label>
              <Input
                id={field.name}
                placeholder={m.max()}
                value={field.state.value}
                onFocus={() => setFocusedField(field.name)}
                onBlur={() => {
                  setFocusedField(null)
                  field.handleBlur()
                }}
                onChange={(e) => field.handleChange(e.target.value)}
                className={showError ? 'border-destructive focus-visible:ring-destructive' : ''}
              />
              {showError && (
                <p className="text-sm text-destructive">{field.state.meta.errors[0]}</p>
              )}
            </div>
          )
        }}
      </form.Field>

      <form.Field
        name="lastName"
        validators={createFieldValidators(ownerFieldsSchema.shape.lastName)}
      >
        {(field: {
          name: string
          state: {
            value: string
            meta: { isTouched: boolean; errors: string[] }
          }
          handleBlur: () => void
          handleChange: (value: string) => void
        }) => {
          const isFocused = focusedField === field.name
          const showError =
            field.state.meta.isTouched && field.state.meta.errors.length > 0 && !isFocused
          return (
            <div className="space-y-2">
              <Label htmlFor={field.name}>{m.lastName()}</Label>
              <Input
                id={field.name}
                placeholder={m.doe()}
                value={field.state.value}
                onFocus={() => setFocusedField(field.name)}
                onBlur={() => {
                  setFocusedField(null)
                  field.handleBlur()
                }}
                onChange={(e) => field.handleChange(e.target.value)}
                className={showError ? 'border-destructive focus-visible:ring-destructive' : ''}
              />
              {showError && (
                <p className="text-sm text-destructive">{field.state.meta.errors[0]}</p>
              )}
            </div>
          )
        }}
      </form.Field>
    </>
  )
}

export function EmailField({ form, focusedField, setFocusedField }: FormFieldProps) {
  return (
    <form.Field name="email" validators={createFieldValidators(ownerFieldsSchema.shape.email)}>
      {(field: {
        name: string
        state: {
          value: string
          meta: { isTouched: boolean; errors: string[] }
        }
        handleBlur: () => void
        handleChange: (value: string) => void
      }) => {
        const isFocused = focusedField === field.name
        const showError =
          field.state.meta.isTouched && field.state.meta.errors.length > 0 && !isFocused

        return (
          <div className="space-y-2">
            <Label htmlFor={field.name}>{m.email()}</Label>
            <Input
              id={field.name}
              type="email"
              placeholder={m.johnexamplecom()}
              value={field.state.value}
              onFocus={() => setFocusedField(field.name)}
              onBlur={() => {
                setFocusedField(null)
                field.handleBlur()
              }}
              onChange={(e) => field.handleChange(e.target.value.trim())}
              className={showError ? 'border-destructive focus-visible:ring-destructive' : ''}
            />
            {showError && <p className="text-sm text-destructive">{field.state.meta.errors[0]}</p>}
          </div>
        )
      }}
    </form.Field>
  )
}

interface BusinessInfoFieldsProps extends FormFieldProps {
  isClub: boolean
  onIsClubChange: (isClub: boolean) => void
}

export function BusinessInfoFields({
  form,
  focusedField,
  setFocusedField,
  isClub,
  onIsClubChange,
}: BusinessInfoFieldsProps) {
  const isClubId = useId()

  return (
    <>
      <form.Field
        name="companyName"
        validators={createFieldValidators(businessFieldsSchema.shape.companyName)}
      >
        {(field: {
          name: string
          state: {
            value: string
            meta: { isTouched: boolean; errors: string[] }
          }
          handleBlur: () => void
          handleChange: (value: string) => void
        }) => {
          const isFocused = focusedField === field.name
          const showError =
            field.state.meta.isTouched && field.state.meta.errors.length > 0 && !isFocused
          return (
            <div className="space-y-2">
              <Label htmlFor={field.name}>{m.tradeNameBrandName()}</Label>
              <Input
                id={field.name}
                placeholder={m.myCompanyLlc()}
                value={field.state.value}
                onFocus={() => setFocusedField(field.name)}
                onBlur={() => {
                  setFocusedField(null)
                  field.handleBlur()
                }}
                onChange={(e) => field.handleChange(e.target.value)}
                className={showError ? 'border-destructive focus-visible:ring-destructive' : ''}
              />
              {showError && (
                <p className="text-sm text-destructive">{field.state.meta.errors[0]}</p>
              )}
            </div>
          )
        }}
      </form.Field>

      <form.Field
        name="legalName"
        validators={createFieldValidators(businessFieldsSchema.shape.legalName)}
      >
        {(field: {
          name: string
          state: {
            value: string
            meta: { isTouched: boolean; errors: string[] }
          }
          handleBlur: () => void
          handleChange: (value: string) => void
        }) => {
          const isFocused = focusedField === field.name
          const showError =
            field.state.meta.isTouched && field.state.meta.errors.length > 0 && !isFocused
          return (
            <div className="space-y-2">
              <Label htmlFor={field.name}>{m.officialCompanyNameCompanyRegister()}</Label>
              <Input
                id={field.name}
                placeholder={m.exampleCompanyLimitedLiabilityCompany()}
                value={field.state.value}
                onFocus={() => setFocusedField(field.name)}
                onBlur={() => {
                  setFocusedField(null)
                  field.handleBlur()
                }}
                onChange={(e) => field.handleChange(e.target.value)}
                className={showError ? 'border-destructive focus-visible:ring-destructive' : ''}
              />
              {showError && (
                <p className="text-sm text-destructive">{field.state.meta.errors[0]}</p>
              )}
            </div>
          )
        }}
      </form.Field>

      <div className="flex items-center space-x-2">
        <Checkbox
          id={isClubId}
          checked={isClub}
          onCheckedChange={(checked) => {
            const nextIsClub = !!checked
            onIsClubChange(nextIsClub)
            // deleteField, not resetField: a Field's mount() returns an empty
            // cleanup, so unmounting never unregisters it. The hidden field
            // stays in fieldInfo, handleSubmit re-runs its required validator
            // on the empty value, and the club submit dies with the error on
            // an invisible field. deleteField drops fieldInfo + meta + value.
            if (nextIsClub) {
              form.deleteField('registrationNumber')
            } else {
              form.setFieldValue('registrationNumber', '')
            }
          }}
        />
        <label htmlFor={isClubId} className="text-sm font-medium leading-none cursor-pointer">
          {m.thisIsAClubOrAssociation()}
        </label>
      </div>

      {!isClub && (
        <form.Field
          name="registrationNumber"
          validators={createFieldValidators(inviteBusinessSchema.shape.registrationNumber)}
        >
          {(field: {
            name: string
            state: {
              value: string
              meta: { isTouched: boolean; errors: string[] }
            }
            handleBlur: () => void
            handleChange: (value: string) => void
          }) => {
            const isFocused = focusedField === field.name
            const showError =
              field.state.meta.isTouched && field.state.meta.errors.length > 0 && !isFocused
            return (
              <div className="space-y-2">
                <Label htmlFor={field.name}>{m.companyRegistrationNumberVatNumber()}</Label>
                <Input
                  id={field.name}
                  placeholder={m.atu12345678()}
                  value={field.state.value}
                  onFocus={() => setFocusedField(field.name)}
                  onBlur={() => {
                    setFocusedField(null)
                    field.handleBlur()
                  }}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className={showError ? 'border-destructive focus-visible:ring-destructive' : ''}
                />
                {showError && (
                  <p className="text-sm text-destructive">{field.state.meta.errors[0]}</p>
                )}
              </div>
            )
          }}
        </form.Field>
      )}

      {isClub && (
        <p className="text-sm text-muted-foreground">{m.clubsDoNotRequireARegistrationNumber()}</p>
      )}
    </>
  )
}

const CONSENTS = [
  {
    name: 'acceptPrivacyPolicy',
    href: 'https://pulsify.at/privacy',
    label: () => m.privacyPolicy(),
  },
  {
    name: 'acceptTerms',
    href: 'https://pulsify.at/terms',
    label: () => m.termsAndConditions(),
  },
] as const

/** The privacy policy and terms checkboxes every business sign-up needs. */
export function ConsentFields({ form }: Pick<FormFieldProps, 'form'>) {
  return (
    <div className="space-y-3">
      {CONSENTS.map((consent) => (
        <form.Field key={consent.name} name={consent.name}>
          {(field: {
            name: string
            state: { value: boolean }
            handleChange: (value: boolean) => void
          }) => (
            <div className="flex items-center gap-2">
              <Checkbox
                id={field.name}
                checked={field.state.value}
                onCheckedChange={(checked) => field.handleChange(checked === true)}
              />
              <label htmlFor={field.name} className="font-medium text-sm leading-none">
                {m.iAcceptThe()}{' '}
                <a
                  href={consent.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={authLinkClassName}
                >
                  {consent.label()}
                </a>
              </label>
            </div>
          )}
        </form.Field>
      ))}
    </div>
  )
}
